import { AlertTriangle, Inbox, RotateCw, SearchX, WifiOff } from 'lucide-react';
import type * as React from 'react';
import { ApiError } from '@/api/client';
import { Button } from './button';
import { IconCircle } from './icon-circle';

function Frame({
  icon,
  title,
  body,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  body?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      <IconCircle>{icon}</IconCircle>
      <div className="space-y-1">
        <p className="font-semibold text-ink">{title}</p>
        {body && <p className="mx-auto max-w-sm text-sm text-muted">{body}</p>}
      </div>
      {action}
    </div>
  );
}

/**
 * Empty is not an error, and "no records yet" is not "no matches" — telling a
 * new user their search found nothing when they have simply not added anyone is
 * the difference between guidance and a dead end.
 */
export function EmptyState({ title, body, action }: { title: string; body?: string; action?: React.ReactNode }) {
  return <Frame icon={<Inbox className="size-5" />} title={title} body={body} action={action} />;
}

export function NoResultsState({ query, onClear }: { query: string; onClear: () => void }) {
  return (
    <Frame
      icon={<SearchX className="size-5" />}
      title={`No matches for “${query}”`}
      body="Try a shorter search — the last four digits of a phone number will match."
      action={
        <Button variant="secondary" size="sm" onClick={onClear}>
          Clear search
        </Button>
      }
    />
  );
}

/**
 * The message depends on WHY it failed. A network drop, a server outage and a
 * rejected request need different words and different offers — "Something went
 * wrong. Retry." for all three tells the user nothing they can act on.
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const api = error instanceof ApiError ? error : null;
  const offline = api?.status === 0 || (typeof navigator !== 'undefined' && !navigator.onLine);

  let title = 'Could not load this';
  let body = api?.message ?? 'An unexpected error occurred.';
  let icon = <AlertTriangle className="size-5 text-danger" />;

  if (offline) {
    title = 'You appear to be offline';
    body = 'Check your connection — nothing was lost.';
    icon = <WifiOff className="size-5 text-warn" />;
  } else if (api?.status === 503) {
    title = 'The service is briefly unavailable';
    body = 'The database is not reachable right now. This usually clears on its own.';
  } else if (api?.status === 403) {
    title = 'You do not have access to this';
    body = api.message;
  } else if (api?.status === 404) {
    title = 'Not found';
    body = api.message;
  }

  return (
    <Frame
      icon={icon}
      title={title}
      body={body}
      action={
        onRetry && (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            <RotateCw /> Try again
          </Button>
        )
      }
    />
  );
}
